import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { validateEmail, markEmailRegistered } from '../utils/emailValidator'

export default function Newsletter() {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // 🛡️ Validate email toàn diện (format, disposable, rate limit)
      const validation = validateEmail(email, true)
      if (!validation.valid) {
        setError(validation.error)
        setLoading(false)
        return
      }

      // Normalize email
      const normalizedEmail = email.trim().toLowerCase()

      // Check if email already exists
      const q = query(
        collection(db, 'newsletterSubscriptions'),
        where('email', '==', normalizedEmail)
      )
      const querySnapshot = await getDocs(q)

      if (!querySnapshot.empty) {
        setError(t('newsletter.alreadySubscribed', 'Email này đã được đăng ký!'))
        setLoading(false)
        return
      }

      // Add new subscription
      await addDoc(collection(db, 'newsletterSubscriptions'), {
        email: normalizedEmail,
        subscribedAt: serverTimestamp(),
        active: true,
        source: 'website',
        createdAt: serverTimestamp()
      })

      // Mark registration in localStorage (rate limit)
      markEmailRegistered()

      setSubscribed(true)
      setEmail('')

      setTimeout(() => {
        setSubscribed(false)
      }, 5000)
    } catch (error) {
      console.error('Error subscribing:', error)
      setError(t('newsletter.error', 'Có lỗi xảy ra. Vui lòng thử lại sau.'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="newsletter">
      <div className="container">
        <div className="newsletter-content">
          <div className="newsletter-text">
            <h2>📧 {t('newsletter.title', 'Đăng Ký Nhận Tin')}</h2>
            <p>{t('newsletter.subtitle', 'Nhận thông tin về tours mới và ưu đãi đặc biệt')}</p>
          </div>

          <form onSubmit={handleSubmit} className="newsletter-form">
            <input
              type="email"
              placeholder={t('newsletter.placeholder', 'Nhập email của bạn')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={subscribed || loading}
            />
            <button type="submit" disabled={subscribed || loading}>
              {loading ? `⏳ ${t('newsletter.processing', 'Đang xử lý...')}` : subscribed ? `✓ ${t('newsletter.subscribed', 'Đã đăng ký')}` : t('newsletter.subscribe', 'Đăng ký')}
            </button>
          </form>

          {subscribed && (
            <p className="success-message">
              🎉 {t('newsletter.successMessage', 'Cảm ơn bạn đã đăng ký! Chúng tôi sẽ gửi thông tin mới nhất đến email của bạn.')}
            </p>
          )}

          {error && (
            <p className="error-message">
              ⚠️ {error}
            </p>
          )}
        </div>
      </div>

      <style>{`
        .newsletter {
          padding: 80px 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .newsletter-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 32px;
        }

        .newsletter-text h2 {
          font-size: 2.5rem;
          margin-bottom: 12px;
        }

        .newsletter-text p {
          font-size: 1.2rem;
          opacity: 0.95;
        }

        .newsletter-form {
          display: flex;
          gap: 12px;
          width: 100%;
          max-width: 600px;
        }

        .newsletter-form input {
          flex: 1;
          padding: 16px 24px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          outline: none;
          color: #1f2937;
          background: white;
        }
        
        .newsletter-form input::placeholder {
          color: #9ca3af;
        }

        .newsletter-form input:focus {
          box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
        }

        .newsletter-form button {
          padding: 16px 32px;
          background: white;
          color: #667eea;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s;
          white-space: nowrap;
        }

        .newsletter-form button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
        }

        .newsletter-form button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .success-message {
          background: rgba(255, 255, 255, 0.2);
          padding: 16px 24px;
          border-radius: 12px;
          animation: slideDown 0.3s ease-out;
        }

        .error-message {
          background: rgba(239, 68, 68, 0.9);
          padding: 16px 24px;
          border-radius: 12px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .newsletter-text h2 {
            font-size: 2rem;
          }

          .newsletter-form {
            flex-direction: column;
            gap: 16px;
          }

          .newsletter-form button {
            width: 100%;
          }
        }
      `}</style>
    </section>
  )
}

